import { RefreshCw } from "lucide-react";
import { useId, type ReactNode } from "react";
import { Link } from "react-router";

import {
  versionSummaryFor,
  type AssetVersionSummary,
  type LibraryAsset,
} from "@/api/library";
import { type AdoptionDecision } from "@/lib/adoptions";
import { pathwaySelectionLabel } from "@/lib/pathwayFamilies";
import { formatDate } from "@/lib/format";
import {
  ADOPTION_STATE_DETAIL,
  ADOPTION_STATE_LABEL,
  IDENTITY_ERASED_DETAIL,
  IDENTITY_SOURCE_LABEL,
  LOCK_REASON_DETAIL,
  LOCK_REASON_LABEL,
} from "@/lib/labels";
import {
  ASSET_TYPE_LABELS,
  libraryAssetHref,
  librarySectionHref,
  type LibrarySectionSlug,
} from "@/lib/librarySections";
import type {
  AdoptionUnavailableReason,
  SectionAdoptedItemView,
  SectionAdoption,
  SectionAdoptionState,
} from "@/lib/sectionAdoption";
import { Badge } from "@/ui/Badge";
import { Button } from "@/ui/Button";
import { LoadingState } from "@/ui/states";
import { Tooltip } from "@/ui/Tooltip";

import { PathwaySelectionSummary } from "./PathwaySelectionSummary";

/**
 * Why a write on one adopted item is refused, or `null` where it is allowed.
 *
 * Both verdicts are `lib/adoptions.ts`'s `adoptionRefusal`, computed by the
 * caller from what the SERVICE reported — the missing capability, an unknown
 * asset, the server's lock, the archived rule. This component decides nothing
 * about permission; it renders the decision and disables the control, which is
 * the same relationship `AdoptionRow` has to the same function.
 */
export type AdoptionRefusals = {
  /** Refusal for moving or adding this project's pin on the item. */
  readonly pin: string | null;
  /** Refusal for removing it. */
  readonly unpin: string | null;
};

/**
 * One shared-library item this section may adopt but this project does not pin.
 *
 * The asset and the SERVICE's own reason for refusing a new pin on it, computed
 * by the caller exactly as `refusalsByAssetId` is. This component decides
 * nothing about permission; it renders the decision and disables the control.
 */
export type SectionAdoptionCandidate = {
  readonly asset: LibraryAsset;
  readonly refusal: string | null;
};

/**
 * The change-pin controls, or absent when the block may not offer any.
 *
 * ABSENT IS A STATE, exactly as `onRetry`'s absence is. The caller withholds
 * this whenever the pin set could not be read: every decision here is a
 * whole-set replacement, so a control offered over an unread set could only
 * open a dialog that fails closed.
 *
 * ADOPTING A NEW ITEM IS NO LONGER HERE (#909). The candidate list this type
 * used to carry is `AdoptFromLibraryAction`, the pane's top-right control, for
 * the reason the reference screens give: adoption is the section's primary
 * action and belongs at the top of it, not in the evidence at the bottom. The
 * derivation (`sectionAdoptionCandidates`), the rows (`AvailableRow`) and the
 * confirmation (`AdoptionDecisionDialog`) are unchanged and still the caller's
 * — only the placement moved. What stayed is what acts on an item this section
 * ALREADY reports, which is evidence-adjacent by construction.
 */
export type SectionAdoptionActions = {
  readonly onDecide: (decision: AdoptionDecision) => void;
  /**
   * The refusals for each adopted item this block may offer controls for.
   *
   * AN ITEM ABSENT FROM THIS MAP GETS NO CONTROLS, and that is a state the
   * caller uses rather than an oversight: on a review slug the canonical row
   * beside this block carries the existing-item decision, and a second
   * update/remove control here would be a second authority over one
   * version-sensitive write (Codex, #570 round 2). The item's EVIDENCE is
   * unaffected — it is only the buttons that belong elsewhere.
   */
  readonly refusalsByAssetId: ReadonlyMap<string, AdoptionRefusals>;
};

/**
 * What supplies THIS section, standing inside it (#443).
 *
 * PROP-DRIVEN, and for the reason `AdoptionRow` is: every state this block has
 * to render is a state the live component lane cannot reach. A 200 from
 * `GET /sections/{section}` needs a mounted projection engine, which that lane
 * does not mount, so `current`, `outdated`, `locally overridden` and
 * `not-applicable` are unreachable there — only `unavailable` is. Driving the
 * block from the values `lib/sectionAdoption.ts` computes covers all six,
 * including the four no lane on this machine produces.
 *
 * THE ACTION NOW STANDS BESIDE THE EVIDENCE (#489), AND STILL WRITES NOTHING
 * ITSELF. #443 shipped this block with no control but a retry, on the reasoning
 * that "every route into changing an adoption is #372's `/adoptions` surface".
 * #489 retires that surface as a primary tab — adoption is not a mode of its
 * own — and moves the decision here, where the clinician already is. What did
 * NOT change is the thing that reasoning was protecting: there is still exactly
 * ONE route into changing an adoption. The controls below only call `onDecide`;
 * the caller opens `AdoptionDecisionDialog`, which is #372's own confirmation,
 * reused unchanged, computing the whole-set `PUT /adoptions` replacement and
 * stating its impact before writing. No adoption API is added anywhere, and no
 * second place computes a pin set.
 *
 * TWO DECISIONS SINCE #909, AND WHERE THEIR SUBJECT COMES FROM. Moving and
 * removing a pin are offered per ADOPTED ITEM, because the section itself
 * reported that item: no browser-side guess is involved in acting on something
 * the projection named. Adopting a NEW item never could come from there — an
 * item the project has never pinned is named by no field origin, no matrix ref
 * and no pathway ref — so its candidates came from the catalog, and #909 moved
 * that third control to `AdoptFromLibraryAction` at the top of the pane, which
 * is where the reference screens put it and where an author looks for it. The
 * derivation, the rows and the confirmation are the same code; only the
 * placement changed.
 *
 * THIS BLOCK IS THE PANE'S `Details` CONTENT (#909). Everything below is
 * evidence a support engineer needs and a clinical author does not have to read
 * to adopt a BG domain: which item supplies the section, at which version,
 * against which library head, whether the pin set agrees, and which reads
 * failed. None of it was deleted to reach the compact layout — the audit
 * finding was that it stood in FRONT of the rows, not that it existed.
 *
 * COLOUR IS NEVER THE DISTINCTION. Each state renders its own label text and
 * its own sentence; the badge tone is redundant with both. The ticket asks for
 * six states a clinician can tell apart, and a clinician reading a monochrome
 * screen or listening to a screen reader is still a clinician.
 */
export function SectionAdoptionBlock({
  projectId,
  sectionLabel,
  librarySection,
  adoption,
  versionsByAssetId,
  historyPending = false,
  historyFailedAssetIds,
  failedReads = [],
  onRetry,
  libraryLinkedItemIds,
}: {
  projectId: string;
  /** The section's own display name, so the block names what it describes. */
  sectionLabel: string;
  /**
   * The library section this block's families are catalogued under (#615).
   *
   * REQUIRED, so the browse destination is a decision the caller makes from
   * this section's own adoptable families rather than a default this block
   * falls back to. The `not-applicable` next step used to send every section
   * to bare `/library`, which redirects to the router's first section — a
   * clinician standing in Main concern landed on Pathways (MC-PRJ-08).
   */
  librarySection: LibrarySectionSlug;
  adoption: SectionAdoption;
  /** Version history per adopted asset — the only source of "last changed". */
  versionsByAssetId: ReadonlyMap<string, readonly AssetVersionSummary[]>;
  /** At least one history is still being read. */
  historyPending?: boolean;
  /**
   * The adopted assets whose history failed, BY ASSET.
   *
   * Per asset rather than the single boolean this took until #443's first
   * review round: one failed history used to make every row in the list say
   * its publication date was missing, including the rows that had it.
   */
  historyFailedAssetIds?: ReadonlySet<string>;
  /**
   * The reads a retry would reach, named for the clinician, or empty.
   *
   * Non-empty exactly when `onRetry` is supplied, and it is what lets the
   * recovery sentence say WHICH read failed instead of restating the headline.
   */
  failedReads?: readonly string[];
  /**
   * Re-reads whichever server responses failed transiently, or absent when
   * none did.
   *
   * ABSENT IS A STATE, not a default. A block can be `unavailable` over three
   * reads that all succeeded — `library_incomplete` and `version_unreported`
   * are both that — and a retry there re-issues requests whose answer is
   * already known, which is the control #4 forbids. The caller supplies this
   * only when a failed read exists that a retry could clear.
   */
  onRetry?: () => void;
  /**
   * Asset ids whose exact `View in Clinical library` another surface on the
   * same page already carries (#615), or absent where no other surface does.
   *
   * ONE ASSET, ONE NAVIGATION AFFORDANCE PER PAGE — #570's consolidation,
   * extended from the controls to the one read-only affordance an item
   * carries. Two surfaces link the same pins beside this block: a canonical
   * review row's menu (`SectionDiagnostics`, every bounded slug), and the
   * whole-pin-set cards below the Clinical summary's panel
   * (`PinConsequences`), which carry the link and NO decision. This block
   * withholds only the link for an id here; what it withholds of the
   * CONTROLS is the panel's decision, made from the rows alone — so the
   * Summary's cards cannot strip an item's update/remove by being named here.
   *
   * SEPARATE FROM `actions`, deliberately: `actions` is absent altogether
   * whenever the pin set could not be read, and the review row still renders
   * then, with its own link. The item's library link is not gated on the
   * pin-set read, so the coverage fact needs its own channel or the two
   * surfaces offer one asset's navigation twice exactly when `actions` is
   * withheld.
   */
  libraryLinkedItemIds?: ReadonlySet<string>;
}) {
  const headingId = useId();
  const { state } = adoption;

  return (
    // NO CARD CHROME OF ITS OWN since #909. This block is the CONTENT of
    // `ProjectSectionPane`'s Details disclosure, which draws the box; a second
    // bordered card nested inside it read as a panel that had been demoted
    // rather than as the evidence behind the rows above.
    <section aria-labelledby={headingId}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
        <h3
          id={headingId}
          className="min-w-0 flex-1 text-sm font-semibold text-foreground"
        >
          What supplies {sectionLabel}
        </h3>
        <Badge tone={TONE_BY_STATE[state]}>{ADOPTION_STATE_LABEL[state]}</Badge>
      </div>

      <p className="mt-2 text-sm text-muted-foreground">
        {adoption.adoptedIdentityErased
          ? IDENTITY_ERASED_DETAIL
          : ADOPTION_STATE_DETAIL[state]}
      </p>

      {state === "unavailable" ? (
        <p className="mt-3 text-sm text-foreground">
          {UNAVAILABLE_DETAIL[adoption.reason ?? "projection_unread"]}
        </p>
      ) : null}

      {adoption.adoptedIdentityErased ? (
        // The section reports local changes and reports no item supplying it.
        // Saying "adopts nothing" here would be a statement about the project's
        // configuration built on the service having WITHHELD an identity:
        // `field_origin` is dropped for every draft-supplied value, so an
        // adoption this project has fully overridden leaves its overrides in
        // the response and its identity out of it.
        //
        // WHY THE LIMITATION IS PRINTED RATHER THAN SOLVED. A fully overridden
        // adoption and a section that adopts nothing and was then edited
        // locally produce the SAME response, and no authoritative read
        // separates them: `AdoptionPin` is `{assetId, version, assetType}` with no section
        // attribution, and `SECTION_BY_ASSET_TYPE` is the library rail's
        // navigation grouping rather than a contract join — either would be the
        // browser-side inference this ticket's non-goals rule out. The durable
        // fix is an Editor Service projection that keeps adoption attribution
        // separately from the effective value's origin, which is a contract
        // change and out of this pull request's scope.
        //
        // THE COPY MAY THEREFORE NOT PRESUPPOSE AN ADOPTION. Codex, round 3:
        // "the item supplying them cannot be named here" asserts that such an
        // item exists, which is false in the no-adoption response this block
        // cannot tell itself apart from; and the pin list cannot be "the
        // authoritative answer to which item supplies this section", because a
        // pin carries no section attribution and several pins would leave the
        // question exactly as open. What is reported is an override and an
        // absent supplying identity, so those are the only two things stated —
        // plus the refusal to decide between the two readings, and a link
        // offered for investigation rather than as an answer.
        //
        // #489 RE-POINTS THAT LINK, and does not drop it. It addressed
        // `/adoptions`, which is no longer a surface; the whole-pin-set list a
        // clinician would investigate from is #373's `PinConsequences`, on the
        // project's own Clinical summary section. Same fact, same purpose, an
        // address that still answers.
        <p className="mt-3 text-sm text-foreground">
          The Editor Service reports locally overridden values in this section
          and no item supplying it. It drops a field&apos;s origin as soon as
          this project&apos;s draft supplies the value, so an adoption whose
          every field this project has overridden and a section that adopts
          nothing and was then edited locally arrive here as the same response.
          This block cannot establish which of the two it is showing: it is not
          saying the section adopts nothing, and it is not claiming an adoption
          it cannot see.{" "}
          <Link
            to={PIN_SET_PATH(projectId)}
            className="font-medium text-accent-foreground underline underline-offset-4"
          >
            What this project has adopted
          </Link>{" "}
          lists this project&apos;s pins, to investigate from.
        </p>
      ) : null}

      {onRetry ? (
        <RetryNotice failedReads={failedReads} onRetry={onRetry} />
      ) : null}

      {/* THE PATHWAYS SECTION ALONE, because it is the only one whose contract
          carries a runtime selection: `PathwayRef.selection` is on
          `PathwaysSection` and nowhere else, so `source` — the section's own
          identity SHAPE, reported rather than inferred — is the exact
          discriminator. Testing the rail slug instead would have put an empty
          panel on six sections whose responses cannot answer the question.

          It sits ABOVE the per-item rows deliberately: Always/Dynamic and the
          Dynamic allowance are facts about the SET, and the audit's PW-PRJ-03
          is that the concept was absent from the section, not from one row. The
          per-item line below still states each pin's own selection. */}
      {adoption.source === "pathway_refs" ? (
        <PathwaySelectionSummary items={adoption.items} />
      ) : null}

      {adoption.items.length > 0 ? (
        <>
          <p className="mt-3 text-xs text-muted-foreground">
            Read from {IDENTITY_SOURCE_LABEL[adoption.source]}.
          </p>
          <ul className="mt-3 space-y-3">
            {adoption.items.map((item) => (
              <li key={item.assetId}>
                <AdoptedItem
                  item={item}
                  versions={versionsByAssetId.get(item.assetId)}
                  historyPending={historyPending}
                  historyFailed={
                    historyFailedAssetIds?.has(item.assetId) ?? false
                  }
                  pinSetRead={adoption.pinSetRead}
                  linkedElsewhere={
                    libraryLinkedItemIds?.has(item.assetId) ?? false
                  }
                  converging={state === "converging"}
                />
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {adoption.overriddenPaths.length > 0 ? (
        // Rendered whatever the headline state, so precedence never hides it:
        // an outdated section that is ALSO overridden reports both.
        //
        // "away from what it adopted" is true wherever an adopted item IS
        // named, and unsupportable in the erased case for the same reason the
        // paragraph above is careful: there may be no adoption to have moved
        // away from. Only the clause changes between the two.
        //
        // #488: the COUNT, not the addresses. This sentence used to end with
        // `overriddenPaths.join(", ")` in monospace, which is a list of dotted
        // projection addresses on a Project route. What the sentence is for —
        // that this project has moved away from what it adopted, and how far —
        // survives without them. `overriddenPaths` is untouched on the
        // derivation and still read by validation and support.
        <p className="mt-3 text-sm text-foreground">
          {adoption.adoptedIdentityErased
            ? "This project's draft supplies "
            : "This project changes "}
          {adoption.overriddenPaths.length === 1
            ? "one value"
            : `${adoption.overriddenPaths.length} values`}
          {adoption.adoptedIdentityErased
            ? " in this section, per the Editor Service."
            : " away from what it adopted, per the Editor Service."}
        </p>
      ) : null}

      <p className="mt-4 text-sm">
        <Link
          to={
            !adoption.adoptedIdentityErased && state === "not-applicable"
              ? librarySectionHref(librarySection)
              : PIN_SET_PATH(projectId)
          }
          className="font-medium text-accent-foreground underline underline-offset-4"
        >
          {adoption.adoptedIdentityErased
            ? NEXT_STEP_IDENTITY_ERASED
            : NEXT_STEP_BY_STATE[state]}
        </Link>
      </p>
    </section>
  );
}

/**
 * The badge's colour, which is deliberately redundant with the label beside it.
 *
 * `not-applicable` is `outline` rather than a warning tone: adopting nothing is
 * a legitimate configuration, not a problem to be flagged.
 */
const TONE_BY_STATE = {
  current: "success",
  converging: "accent",
  outdated: "warning",
  overridden: "accent",
  unavailable: "destructive",
  "not-applicable": "outline",
} as const satisfies Record<
  SectionAdoptionState,
  "success" | "warning" | "accent" | "destructive" | "outline"
>;

/**
 * The whole-pin-set view, which is where "what does this project adopt" is
 * answered now that `/adoptions` is no longer a surface (#489).
 *
 * #373's `PinConsequences`, on the project's own Clinical summary section: it
 * lists every pin this project holds with its name, type, adopted version and
 * whether the library has moved past it. It is READ-ONLY, which is exactly
 * right for a link offered as somewhere to look — the decisions live on the
 * items themselves, in the controls above.
 */
const PIN_SET_PATH = (projectId: string) =>
  `/projects/${encodeURIComponent(projectId)}/config/summary`;

/**
 * The one safe next step per state.
 *
 * Until #489 every one of these addressed #372's `/adoptions` surface, because
 * that was the only route into changing an adoption. That route now runs
 * through the controls on the items above, so these sentences are what remains:
 * somewhere to LOOK, not somewhere to act.
 *
 * `outdated` no longer says "move the pin in Adopted content" — the pin moves
 * with the button beside the item, and a sentence sending a clinician elsewhere
 * to do what is already in front of them is worse than no sentence. It points
 * at the same whole-set view as its neighbours instead.
 *
 * `not-applicable` is the one state with a different destination, and it always
 * was the odd one: a section that adopts nothing has no item to look at, so the
 * only useful place to go is the shared library itself.
 */
const NEXT_STEP_BY_STATE = {
  current: "See every item this project has adopted",
  converging: "See every item this project has adopted",
  outdated: "See every item this project has adopted",
  overridden: "Review this project's overrides against what it adopted",
  unavailable: "See every item this project has adopted",
  "not-applicable": "Browse the shared clinical library",
} as const satisfies Record<SectionAdoptionState, string>;

/**
 * The next step for an `overridden` section whose supplying identity the
 * service did not report.
 *
 * `NEXT_STEP_BY_STATE.overridden` invites a comparison "against what it
 * adopted", and this is the one state where there may be nothing adopted to
 * compare against. What the pin list actually offers here is somewhere to start
 * looking, so that is what the link says it is.
 */
const NEXT_STEP_IDENTITY_ERASED = "See which items this project has adopted";

/**
 * Why the state could not be read, and the read that would answer it.
 *
 * Four causes, named separately, in `ProjectionNotice`'s wording discipline: a
 * surface that renders an unread projection and an unnamed asset identically is
 * telling a clinician the same thing about two different problems with two
 * different owners. Two of the four — `library_incomplete` and
 * `version_unreported` — are reads that SUCCEEDED and answered without the fact
 * in question, which is why the recovery below is keyed on the failed reads
 * rather than on this state.
 */
const UNAVAILABLE_DETAIL = {
  projection_unread:
    "The Editor Service did not return this section's effective configuration, so what supplies it is not known here. This is not a statement that the section adopts nothing.",
  library_unread:
    "The shared library catalog did not load, so whether a newer version of these items exists cannot be established. The identities and versions below are the Editor Service's own.",
  library_incomplete:
    "The shared library did not return every item this section names, so whether a newer version exists cannot be established for all of them. The identities and versions below are the Editor Service's own.",
  version_unreported:
    "The Editor Service named an item supplying this section but no version for it, so whether this project runs the library's current version cannot be established. Nothing here fills that in.",
} as const satisfies Record<AdoptionUnavailableReason, string>;

/**
 * The one recovery this block contributes, and the reads it would reach.
 *
 * NOT TIED TO THE HEADLINE STATE, and that is the whole point of it. A
 * transient `GET /adoptions` failure beside a healthy projection and catalog
 * leaves the block reading `current` or `outdated` — a true headline — with an
 * unread pin set underneath it and, until #443's first review round, no control
 * anywhere on the surface to re-read it. The clinician's only recovery was to
 * leave the route. The caller supplies this component's props from the failed
 * READS, so every transient failure reaches a button whatever the state above
 * it says, and a state reached over successful reads reaches none.
 *
 * Named "Read it again" rather than "Try again", for the reason the panel
 * states: the surfaces this block sits on already render a "Try again" for the
 * section query, and two identically named controls for one failure are
 * ambiguous to a clinician and unresolvable to a screen reader.
 */
export function RetryNotice({
  failedReads,
  onRetry,
}: {
  failedReads: readonly string[];
  onRetry: () => void;
}) {
  return (
    <div className="mt-3 space-y-3">
      {failedReads.length > 0 ? (
        <p className="text-sm text-foreground">
          {failedReads.length === 1
            ? `${capitalize(failedReads[0] ?? "")} could not be read.`
            : `These reads did not answer: ${failedReads.join("; ")}.`}
        </p>
      ) : null}
      <Button variant="outline" onClick={onRetry}>
        <RefreshCw aria-hidden="true" className="h-4 w-4" />
        Read it again
      </Button>
    </div>
  );
}

function capitalize(sentence: string): string {
  return sentence.charAt(0).toUpperCase() + sentence.slice(1);
}

/**
 * One adopted item: which shared-library item, at which version, and when it
 * last changed.
 *
 * Every value rendered is a value the service returned. The pinned version is
 * the section projection's or the refs list's; the head is the catalog's
 * `latestVersion`; the dates are `AssetVersionSummary.createdAt`, looked up by
 * equality on the version label rather than by position in the history.
 *
 * "Version/revision" reads as the immutable pin version alone. There is no
 * per-adoption revision in the contract — `AdoptionPin` is `{assetId, version, assetType}`
 * and `adoptionsVersion` identifies the whole pin set for optimistic
 * concurrency, not one adoption — so a "revision" here would be a number this
 * surface invented. Stated rather than silently dropped.
 */
function AdoptedItem({
  item,
  versions,
  historyPending,
  historyFailed,
  pinSetRead,
  linkedElsewhere,
  converging,
}: {
  item: SectionAdoptedItemView;
  versions: readonly AssetVersionSummary[] | undefined;
  historyPending: boolean;
  /** Whether THIS asset's version history failed. Never the set's. */
  historyFailed: boolean;
  /** Whether `GET /adoptions` answered at all. */
  pinSetRead: boolean;
  /**
   * Whether another surface on this page — a canonical review row's menu, or
   * a whole-pin-set card — already offers this item's `View in Clinical
   * library` (#570, #615). The evidence below stays either way; only the
   * affordance that surface duplicates is withheld.
   */
  linkedElsewhere: boolean;
  /** Whether the section projection and pin set are from different tokens. */
  converging: boolean;
}) {
  const name = item.asset?.name ?? item.assetId;
  // The item's family, from the adoption/catalog contract's two statements of
  // it: the catalog's row, or the stored pin's own `assetType` (#615). Never
  // from the section this block renders in.
  const family = item.asset?.assetType ?? item.pinnedAssetType;
  const adopted = versionSummaryFor(versions, item.version);
  const head = versionSummaryFor(versions, item.latestVersion);

  return (
    <div className="rounded-md border border-border px-3 py-2">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="min-w-0 flex-1 text-sm font-medium text-foreground">
          {name}
        </span>
        <Badge tone="outline">
          {family ? ASSET_TYPE_LABELS[family] : "Unknown type"}
        </Badge>
        <Badge
          tone={converging ? "accent" : item.behind ? "warning" : "accent"}
        >
          {item.version === null
            ? "Version not reported"
            : converging
              ? `Section reports ${item.version}`
              : `Adopted ${item.version}`}
        </Badge>
        {item.effectiveLock ? (
          <Badge tone="warning">{LOCK_REASON_LABEL[item.effectiveLock]}</Badge>
        ) : null}
      </div>

      <dl className="mt-2 space-y-1 text-xs text-muted-foreground">
        {item.variantOf ? (
          <Row term="Variant of">
            <span className="font-mono">{item.variantOf}</span>
          </Row>
        ) : null}

        {/* ONLY WHERE THE SECTION CAN CARRY ONE. `selection` is `null` for every
            non-Pathway pin by contract, and rendering "Not an active runtime
            selection" against a Rule or a BG set would state a Pathway fact
            about a family that has none. `family` is the pin's own `assetType`
            (#615), never the section this row was rendered in. */}
        {family === "pathway" || family === "pathway_variant" ? (
          <Row term="Runtime selection">
            {pathwaySelectionLabel(item.selection)}
            {item.selection ? " — decided by the Editor Service." : "."}
          </Row>
        ) : null}

        <Row term="Library head">
          {item.latestVersion === null ? (
            "Not reported — the shared library did not name this item."
          ) : item.version === null ? (
            // AHEAD OF `behind`, because `behind` is FALSE here and means
            // nothing. `isBehind` needs both versions, so an origin that names
            // the item without naming a version cannot be behind — and the
            // final branch reads that absence of evidence as evidence, printing
            // "this project adopts the library's head" beside a badge that says
            // the version was not reported. The head is a fact about the
            // LIBRARY; which version this project runs is the thing the service
            // declined to say.
            `${item.latestVersion} — the shared library's current version. Whether this project runs it is not established, because no adopted version was reported.`
          ) : converging ? (
            `${item.latestVersion} — the shared library's current version. This section projection is still catching up to the current pin set.`
          ) : item.behind ? (
            <>
              {item.latestVersion}, published{" "}
              {head ? formatDate(head.createdAt) : "on a date not reported"}
              {head?.createdBy ? ` by ${head.createdBy}` : ""}
              {head?.note ? ` — ${head.note}` : ""}
            </>
          ) : (
            `${item.latestVersion} — this project adopts the library's head.`
          )}
        </Row>

        <Row term="Adopted version published">
          {historyPending && adopted === undefined ? (
            <LoadingState
              label={`Loading the version history for ${name}`}
              rows={1}
              className="max-w-xs"
            />
          ) : adopted ? (
            <>
              {formatDate(adopted.createdAt)} by {adopted.createdBy}
              {adopted.note ? ` — ${adopted.note}` : ""}
            </>
          ) : historyFailed ? (
            "This item's version history did not load, so when this version was published is not shown."
          ) : (
            "Not reported."
          )}
        </Row>

        {!pinSetRead ? (
          // `pinnedVersion: null` means two different things and only one of
          // them is a fact. Without the pin set, the honest statement is that
          // it was not read — saying the project does not pin this item would
          // be an assertion on no evidence.
          <Row term="This project's pin set">
            Could not be read, so whether this project pins the item is not
            shown.
          </Row>
        ) : item.pinnedVersion === null ? (
          // The projection and `GET /adoptions` are one subject read from two
          // ends. Saying the pin set does not carry this item is a fact worth
          // rendering: it is exactly the disagreement two independent reads
          // would otherwise hide.
          <Row term="This project's pin set">
            Does not name this item. It reaches the section from the base pack
            or a seed rather than from a pin this project holds.
          </Row>
        ) : item.pinnedVersion !== item.version ? (
          <Row term="This project's pin set">
            {converging ? (
              "The loaded pin set has a different token from this section projection, so this row is waiting for the projection read to settle."
            ) : (
              <>
                Names {item.pinnedVersion}, which is not the version this
                section reports. Both values are the Editor Service&apos;s own.
              </>
            )}
          </Row>
        ) : null}

        {item.fieldPaths.length > 0 ? (
          // #488: HOW MUCH this item supplies, not the dotted addresses of it.
          //
          // This row rendered `item.fieldPaths.join(", ")` in monospace, which
          // put projection addresses on a Project route. The count is what the
          // row was for — evidence that the pin reaches this section, and how
          // far — and it survives the amendment where the addresses do not.
          //
          // The derivation is untouched: `lib/sectionAdoption.ts` still
          // computes `fieldPaths`, and the validation and support paths that
          // read it are unaffected. Only the rendering changed.
          <Row term="Supplies">
            {item.fieldPaths.length === 1
              ? "1 value in this section"
              : `${item.fieldPaths.length} values in this section`}
          </Row>
        ) : null}

        {item.effectiveLock ? (
          <Row term="Locked">{LOCK_REASON_DETAIL[item.effectiveLock]}</Row>
        ) : null}
      </dl>

      {family !== null && !linkedElsewhere ? (
        // The exact library page of the adopted asset (#615). Derived from the
        // family the adoption/catalog contract states — the catalog's row, or
        // the stored pin's own `assetType` when the catalog did not answer —
        // never the section this block renders in, and NOT gated on
        // `actions`: the controls are withheld when the pin set could not be
        // read because every decision is a whole-set write, but navigation to
        // shared clinical content writes nothing and stays. Absent in exactly
        // two cases. When NEITHER the catalog nor the pin set named the item,
        // because without a family there is no route to build and a guessed
        // slug is the defect this link exists to remove. And when another
        // surface on this page already carries the same link — a canonical
        // review row's menu, or the Clinical summary's whole-pin-set card —
        // because one asset is one affordance wherever the page reaches it
        // (#570): for navigation exactly as for the controls.
        <p className="mt-2 text-sm">
          <Link
            to={libraryAssetHref(family, item.assetId)}
            className="font-medium text-accent-foreground underline underline-offset-4"
          >
            {`View ${name} in Clinical library`}
          </Link>
        </p>
      ) : null}

      {/*
        NO CONTROLS HERE SINCE #909's SECOND ROUND. This row is evidence, and
        evidence is what the pane collapses; the decisions it used to carry are
        `SectionAdoptedItemActions`, rendered in the section body where an
        author meets them without expanding anything. The `actions` prop stays
        because `refusalsByAssetId` is still what says which items a canonical
        row speaks for, which this row reads for nothing else — see the note on
        the prop.
      */}
    </div>
  );
}

/**
 * The two decisions this block may offer about one adopted item (#489).
 *
 * WHICH TWO, AND WHY NOT A THIRD.
 *
 *  * **Move the pin to the library's head** — offered only where the item is
 *    `behind` and the catalog actually named a head. It is `AdoptionDecision`'s
 *    `pin` mode, so it is an ADD where this project holds no pin on the item and
 *    a MOVE where it does; `adoptionRefusal` already draws that distinction and
 *    the caller passed the result in.
 *  * **Remove this project's pin** — offered only where the pin set names the
 *    item. A section can report an item supplied by the base pack or a seed with
 *    no pin behind it, and offering to remove a pin this project does not hold
 *    would be a control with nothing to act on.
 *
 * There is no "adopt something else into this section" control, and there
 * cannot honestly be one here: `AdoptionPin` carries no section attribution, so
 * choosing a candidate would mean inferring which assets belong to this section
 * from the library rail's navigation grouping — the browser-side inference
 * `lib/sectionAdoption.ts` refuses by name.
 *
 * The wording is `AdoptionRow`'s, deliberately: a clinician meeting the same
 * decision in two places should meet the same sentence, and the refusal shown
 * in the tooltip is the service's own reason rather than a restatement.
 */
/**
 * Whether this item has any decision to offer at all (#909).
 *
 * EXPORTED AND SHARED with `SectionAdoptedItemActions`, which must list exactly
 * the items whose controls would render. Two copies of this rule would let the
 * list show a row with no buttons under it, or hide one that has them.
 */
export function hasAdoptedItemControls(item: SectionAdoptedItemView): boolean {
  return (
    (item.behind && item.latestVersion !== null) || item.pinnedVersion !== null
  );
}

function AdoptedItemControls({
  item,
  name,
  refusals,
  onDecide,
}: {
  item: SectionAdoptedItemView;
  name: string;
  refusals: AdoptionRefusals;
  onDecide: (decision: AdoptionDecision) => void;
}) {
  const head = item.latestVersion;
  const canMove = item.behind && head !== null;
  const canRemove = item.pinnedVersion !== null;

  if (!hasAdoptedItemControls(item)) {
    return null;
  }

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2">
      {canMove && head !== null ? (
        <Tooltip
          content={
            refusals.pin ??
            `Move this project's pin on ${name} from ${item.version ?? "its current version"} to ${head} in one replacement`
          }
        >
          <Button
            size="sm"
            disabled={refusals.pin !== null}
            onClick={() =>
              onDecide({ mode: "pin", assetId: item.assetId, version: head })
            }
          >
            {`Update ${name} to ${head}`}
          </Button>
        </Tooltip>
      ) : null}

      {canRemove ? (
        <Tooltip
          content={refusals.unpin ?? `Remove this project's pin on ${name}`}
        >
          <Button
            variant="outline"
            size="sm"
            disabled={refusals.unpin !== null}
            onClick={() => onDecide({ mode: "unpin", assetId: item.assetId })}
          >
            {`Unpin ${name}`}
          </Button>
        </Tooltip>
      ) : null}
    </div>
  );
}

/**
 * The decisions an adopted item carries, standing in the section body (#909).
 *
 * ## WHY THIS IS NOT IN `Details`
 *
 * #909 moved the adoption evidence into `ProjectSectionPane`'s disclosure, and
 * the first round moved the item CONTROLS with it. That is a regression on the
 * one surface where those controls have no second home: the Clinical summary
 * passes no `canonicalItemIds`, so no compact review row carries an existing
 * item's Update/Unpin there and this block was their only place. Collapsed by
 * default, an author's update and removal for a pinned Summary prompt were
 * invisible until they expanded something labelled `Details` (Codex, round 1).
 *
 * The rule the amendment actually states is that REVISION IDS, RAW EVIDENCE AND
 * ENGINE DETAIL collapse — not that decisions do. So the split is by kind: what
 * a clinical author ACTS on renders in the section body, and what a support
 * engineer READS stays in the disclosure.
 *
 * ## WHY IT IS USUALLY EMPTY, AND THAT IS CORRECT
 *
 * The caller fills `refusalsByAssetId` only for items no canonical review row
 * speaks for (#570). On the six review slugs that map is normally empty, this
 * component renders nothing, and the section shows exactly one control per
 * decision — which is the consolidation #570 exists to protect. It fills on the
 * Clinical summary, and on any item a bounded response did not name.
 *
 * It decides nothing itself: the refusals, the target version and the write are
 * all the caller's, and every button here only calls `onDecide`.
 */
/**
 * Whether ANY adopted item here would render a decision (#909, Codex round 2).
 *
 * EXPORTED because the caller has to know before it builds the slot. On the six
 * review slugs `canonicalItemIds` empties the refusal map, so this component
 * returns `null` — while a `<>…</>` wrapping it stays truthy, and
 * `ProjectSectionPane` then rendered an empty `space-y-3` div that took a
 * margin from its parent and put a blank gap above the rows. A caller that asks
 * this first renders no slot at all.
 */
export function hasSectionAdoptedItemActions(
  items: readonly SectionAdoptedItemView[],
  actions: SectionAdoptionActions,
): boolean {
  return items.some((item) => itemHasActions(item, actions));
}

/** The one membership rule both the predicate and the list read. */
function itemHasActions(
  item: SectionAdoptedItemView,
  actions: SectionAdoptionActions,
): boolean {
  return (
    actions.refusalsByAssetId.has(item.assetId) && hasAdoptedItemControls(item)
  );
}

export function SectionAdoptedItemActions({
  items,
  actions,
}: {
  items: readonly SectionAdoptedItemView[];
  actions: SectionAdoptionActions;
}) {
  const rows = items.filter((item) => itemHasActions(item, actions));

  if (rows.length === 0) {
    return null;
  }

  return (
    <ul className="space-y-2">
      {rows.map((item) => {
        const name = item.asset?.name ?? item.assetId;
        const refusals = actions.refusalsByAssetId.get(item.assetId);
        return (
          // THE CONTROLS ALONE, WITH NO LABEL BESIDE THEM. Every button here
          // already carries the item in its own text — `Update <name> to v3`,
          // `Unpin <name>` — which is the rule `AdoptedItemControls` states and
          // the reason a fifteen-item section is not fifteen buttons called
          // `Unpin`. A name repeated beside them would say the same thing
          // twice on one row and, with the evidence open, a third time on the
          // page.
          <li key={item.assetId}>
            {refusals ? (
              <AdoptedItemControls
                item={item}
                name={name}
                refusals={refusals}
                onDecide={actions.onDecide}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}

function Row({ term, children }: { term: string; children: ReactNode }) {
  return (
    <div className="flex flex-wrap gap-x-2">
      <dt className="font-medium text-foreground">{term}:</dt>
      <dd className="min-w-0">{children}</dd>
    </div>
  );
}
